// warehouseGeometry — pure geometry helpers for the WarehouseMapEditor
// canvas. Everything operates in viewBox (map) units; no Vue, no DOM.

// Round to grid multiples. A non-positive grid disables snapping.
export function snapPoint(p, grid) {
  if (!grid || grid <= 0) return { x: p.x, y: p.y };
  return {
    x: Math.round(p.x / grid) * grid,
    y: Math.round(p.y / grid) * grid,
  };
}

// Replace each axis with the nearest edge value within `threshold`
// (independently per axis). `edges`: [{ axis: "x"|"y", value }].
export function snapToEdges(p, edges, threshold) {
  const out = { x: p.x, y: p.y };
  for (const axis of ["x", "y"]) {
    let best = null;
    for (const edge of edges || []) {
      if (edge.axis !== axis) continue;
      const dist = Math.abs(edge.value - p[axis]);
      if (dist <= threshold && (best === null || dist < best.dist)) {
        best = { value: edge.value, dist };
      }
    }
    if (best) out[axis] = best.value;
  }
  return out;
}

// Project `p` onto the horizontal or vertical line through `prev`,
// whichever is nearer. A perfect tie goes horizontal.
export function orthoConstrain(prev, p) {
  const dx = Math.abs(p.x - prev.x);
  const dy = Math.abs(p.y - prev.y);
  return dx >= dy ? { x: p.x, y: prev.y } : { x: prev.x, y: p.y };
}

// Candidate snap edges from every zone's geometry except `excludeIndex`:
// rect left/right/top/bottom and every polygon vertex x/y.
export function zoneEdges(zones, excludeIndex = -1) {
  const edges = [];
  (zones || []).forEach((zone, index) => {
    if (index === excludeIndex) return;
    if (zone.shape === "polygon") {
      for (const p of zone.points || []) {
        edges.push({ axis: "x", value: p.x }, { axis: "y", value: p.y });
      }
    } else {
      edges.push(
        { axis: "x", value: zone.x },
        { axis: "x", value: zone.x + zone.w },
        { axis: "y", value: zone.y },
        { axis: "y", value: zone.y + zone.h }
      );
    }
  });
  return edges;
}

// Axis-aligned bounding box for a rect or polygon zone.
export function bbox(zone) {
  if (zone.shape === "polygon") {
    const xs = (zone.points || []).map((p) => p.x);
    const ys = (zone.points || []).map((p) => p.y);
    const x = Math.min(...xs);
    const y = Math.min(...ys);
    return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
  }
  return { x: zone.x, y: zone.y, w: zone.w, h: zone.h };
}

function pointInPolygon(p, points) {
  // Even-odd ray cast.
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i];
    const b = points[j];
    if (
      a.y > p.y !== b.y > p.y &&
      p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x
    ) {
      inside = !inside;
    }
  }
  return inside;
}

// ---- Zone overlap ---------------------------------------------------
//
// Zones may share edges and corners (snapping puts them flush), so only a
// positive-area intersection counts as an overlap. Coordinates are rounded to
// 3 decimals, so a small epsilon absorbs float noise on shared edges.
const EPS = 1e-6;

function zonePolygon(zone) {
  if (zone.shape === "polygon") return zone.points || [];
  const { x, y, w, h } = zone;
  return [
    { x, y },
    { x: x + w, y },
    { x: x + w, y: y + h },
    { x, y: y + h },
  ];
}

const cross = (o, a, b) =>
  (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

function onSegment(p, a, b) {
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  if (len < EPS) return Math.hypot(p.x - a.x, p.y - a.y) < EPS;
  if (Math.abs(cross(a, b, p)) / len > EPS) return false;
  const dot = (p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y);
  return dot >= -EPS && dot <= len * len + EPS;
}

function onBoundary(p, pts) {
  return pts.some((a, i) => onSegment(p, a, pts[(i + 1) % pts.length]));
}

const strictlyInside = (p, pts) =>
  !onBoundary(p, pts) && pointInPolygon(p, pts);

// Segments cross at a single interior point of both (touching, sharing an
// endpoint or running collinear doesn't count).
function properlyCross(a, b, c, d) {
  const d1 = cross(c, d, a);
  const d2 = cross(c, d, b);
  const d3 = cross(a, b, c);
  const d4 = cross(a, b, d);
  return (
    ((d1 > EPS && d2 < -EPS) || (d1 < -EPS && d2 > EPS)) &&
    ((d3 > EPS && d4 < -EPS) || (d3 < -EPS && d4 > EPS))
  );
}

// A point strictly inside a simple (possibly concave) polygon: the middle of
// the first span a horizontal scanline cuts through it, taken between two
// distinct vertex heights so it never passes through a vertex.
function interiorPoint(pts) {
  const ys = [...new Set(pts.map((p) => p.y))].sort((a, b) => a - b);
  if (ys.length < 2) return null;
  const y = (ys[0] + ys[1]) / 2;
  const xs = [];
  pts.forEach((a, i) => {
    const b = pts[(i + 1) % pts.length];
    if (a.y > y !== b.y > y) {
      xs.push(a.x + ((y - a.y) * (b.x - a.x)) / (b.y - a.y));
    }
  });
  xs.sort((a, b) => a - b);
  return xs.length >= 2 ? { x: (xs[0] + xs[1]) / 2, y } : null;
}

function polygonsOverlap(A, B) {
  const edges = (pts) => pts.map((p, i) => [p, pts[(i + 1) % pts.length]]);
  const midpoints = (pts) =>
    edges(pts).map(([a, b]) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }));
  // Any boundary crossing, or any part of one's boundary or interior lying
  // strictly inside the other, means the interiors share area.
  if (
    edges(A).some(([a, b]) =>
      edges(B).some(([c, d]) => properlyCross(a, b, c, d))
    )
  ) {
    return true;
  }
  const probes = (pts) => [...pts, ...midpoints(pts), interiorPoint(pts)];
  return (
    probes(A).some((p) => p && strictlyInside(p, B)) ||
    probes(B).some((p) => p && strictlyInside(p, A))
  );
}

// Indices (ascending) of zones whose area overlaps at least one other zone.
export function overlappingZones(zones) {
  const shapes = (zones || []).map((zone) => {
    const pts = zonePolygon(zone);
    const b = bbox(zone);
    const valid =
      pts.length >= 3 &&
      [b.x, b.y, b.w, b.h].every(Number.isFinite) &&
      b.w > EPS &&
      b.h > EPS;
    return valid ? { pts, b } : null;
  });
  const result = new Set();
  for (let i = 0; i < shapes.length; i++) {
    for (let j = i + 1; j < shapes.length; j++) {
      const A = shapes[i];
      const B = shapes[j];
      if (!A || !B) continue;
      // Bounding boxes must share area, not just an edge.
      if (
        Math.min(A.b.x + A.b.w, B.b.x + B.b.w) - Math.max(A.b.x, B.b.x) <=
          EPS ||
        Math.min(A.b.y + A.b.h, B.b.y + B.b.h) - Math.max(A.b.y, B.b.y) <= EPS
      ) {
        continue;
      }
      if (polygonsOverlap(A.pts, B.pts)) {
        result.add(i);
        result.add(j);
      }
    }
  }
  return [...result].sort((a, b) => a - b);
}

// What is under the point? Zones are tested last-drawn-first (topmost wins).
// Floor vertices need `threshold` (viewBox units) since a point rarely lands
// exactly on a vertex. Returns { type:"zone"|"floorVertex", index } or null.
export function hitTest(model, p, threshold = 0) {
  const zones = model.zones || [];
  for (let i = zones.length - 1; i >= 0; i--) {
    const zone = zones[i];
    const hit =
      zone.shape === "polygon"
        ? pointInPolygon(p, zone.points || [])
        : p.x >= zone.x &&
          p.x <= zone.x + zone.w &&
          p.y >= zone.y &&
          p.y <= zone.y + zone.h;
    if (hit) return { type: "zone", index: i };
  }
  const floor = model.floor || [];
  for (let i = 0; i < floor.length; i++) {
    const dx = floor[i].x - p.x;
    const dy = floor[i].y - p.y;
    if (Math.sqrt(dx * dx + dy * dy) <= threshold) {
      return { type: "floorVertex", index: i };
    }
  }
  return null;
}

// New zone shifted by dx/dy — rect x/y or every polygon point.
export function moveZone(zone, dx, dy) {
  if (zone.shape === "polygon") {
    return {
      ...zone,
      points: (zone.points || []).map((p) => ({
        x: p.x + dx,
        y: p.y + dy,
      })),
    };
  }
  return { ...zone, x: zone.x + dx, y: zone.y + dy };
}

// Resize a rect by moving `corner` to `p`; the opposite corner stays fixed
// and w/h never go below `minSize`.
export function resizeRect(zone, corner, p, minSize = 0) {
  let { x, y, w, h } = zone;
  const right = x + w;
  const bottom = y + h;
  if (corner === "nw" || corner === "sw") {
    x = Math.min(p.x, right - minSize);
    w = right - x;
  } else {
    w = Math.max(minSize, p.x - x);
  }
  if (corner === "nw" || corner === "ne") {
    y = Math.min(p.y, bottom - minSize);
    h = bottom - y;
  } else {
    h = Math.max(minSize, p.y - y);
  }
  return { ...zone, x, y, w, h };
}

// New point array with `index` moved to `p` (works for zone.points or
// model.floor).
export function setVertex(points, index, p) {
  return (points || []).map((pt, i) => (i === index ? { x: p.x, y: p.y } : pt));
}

// New point array with `p` inserted before `index`.
export function insertVertex(points, index, p) {
  const out = (points || []).slice();
  out.splice(index, 0, { x: p.x, y: p.y });
  return out;
}

// New point array without `index`. Refuses to go below 3 points (returns
// the input unchanged) — a polygon needs at least a triangle.
export function removeVertex(points, index) {
  if (!points || points.length <= 3) return points;
  const out = points.slice();
  out.splice(index, 1);
  return out;
}

// Crop the viewBox to the content: the floor's bounds (grown or shrunk),
// extended by any zone that sticks out past the floor. Overhang within half
// the zone stroke (0.05·labelSize) doesn't count, so zones drawn flush with
// the floor edge (float noise, e.g. 180EB's 7.3549… vs 7.35) keep the crop
// exact. Without a floor, fits the zones. Returns the model itself when the
// viewBox already fits (or there's nothing to fit).
export function fitViewBox(model) {
  const floor = model.floor || [];
  const hasFloor = floor.length >= 3;
  const boxes = (model.zones || [])
    .map(bbox)
    .filter((b) => [b.x, b.y, b.w, b.h].every(Number.isFinite));
  if (!hasFloor && !boxes.length) return model;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  if (hasFloor) {
    floor.forEach((p) => {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    });
  }
  const tol = hasFloor ? (model.labelSize || 0) * 0.05 : 0;
  boxes.forEach((b) => {
    if (b.x < minX - tol) minX = b.x;
    if (b.y < minY - tol) minY = b.y;
    if (b.x + b.w > maxX + tol) maxX = b.x + b.w;
    if (b.y + b.h > maxY + tol) maxY = b.y + b.h;
  });
  const r3 = (n) => Math.round(n * 1000) / 1000;
  const viewBox = {
    x: r3(minX),
    y: r3(minY),
    w: r3(maxX - minX),
    h: r3(maxY - minY),
  };
  const vb = model.viewBox || {};
  if (["x", "y", "w", "h"].every((k) => vb[k] === viewBox[k])) return model;
  return { ...model, viewBox };
}

// Vertical labels earn a -90 rotation when the zone is taller than wide AND
// the label still overflows horizontally at that narrower inner width.
// `wrapLabel` is injected so this stays pure (it lives in warehouseSvg).
export function suggestRotation(zone, labelSize, wrapLabel) {
  const { w, h } = bbox(zone);
  if (!(h > w)) return 0;
  const innerW = w - labelSize * 0.5;
  const charW = 0.6 * labelSize;
  return wrapLabel(zone.label, innerW, labelSize).some(
    (line) => line.length * charW > innerW
  )
    ? -90
    : 0;
}
